import { Button, Combobox, Dialog, DialogContent, DialogHeader, DialogTitle, IconChevronLeft, IconInfo, Spinner, Text } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import short from "short-uuid";
import { toast } from "sonner";

import type { Song, Suggestion } from "@/api/queries";

import { useCreateRecording, useUpdateRecordingMeta, useUpdateSuggestion } from "@/api/mutations";
import { useGetMyProfile, useGetMySong, useGetMySongs } from "@/api/queries";
import FormError from "@/components/FormError";
import LoadingSmall from "@/components/LoadingSmall";
import { sanitizeInput, sendEvent, sendProperty } from "@/helpers/helpers";
import useStringSimilarity from "@/hooks/useStringSimilarity";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import SongsCreateUpdate from "./SongsCreateUpdate";

type Step = "choice" | "new" | "existing";

type SongSuggestionActionModalProps = {
  suggestion: Suggestion;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuggestionResolved: () => void;
};

function parseSuggestionThumbUrl(images: Suggestion["images"]): string | null {
  if (typeof images !== "string")
    return null;
  try {
    const parsed = JSON.parse(images) as { url?: string }[];
    return parsed[0]?.url ?? null;
  }
  catch (error) {
    console.warn("[SongSuggestionActionModal] Invalid JSON in suggestion.images:", images, error);
    return null;
  }
}

function SongSuggestionActionModal({ suggestion, open, onOpenChange, onSuggestionResolved }: SongSuggestionActionModalProps) {
  const { t } = useTranslation(["songs", "common"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const profile = useGetMyProfile();
  const { compareTwoStrings } = useStringSimilarity();

  const [step, setStep] = useState<Step>("choice");
  const [songList, setSongList] = useState<Song[]>([]);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [suggestedSong, setSuggestedSong] = useState<Song | null>(null);
  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const songs = useGetMySongs({ aac: selectedAccount });
  const songTemp = useGetMySong({
    aac: selectedAccount,
    wc: selectedSong?.workcode ?? null,
  });

  const queryClient = useQueryClient();
  const updateSuggestionMutation = useUpdateSuggestion();
  const recordingCreateMutation = useCreateRecording();
  const recordingMetaMutation = useUpdateRecordingMeta();

  // Reset to a clean slate whenever the modal opens for a (possibly different) suggestion.
  useEffect(() => {
    if (open) {
      setStep("choice");
      setSelectedSong(null);
      setSuggestedSong(null);
      setMutationErrorMessage("");
      setValidationErrorMessage("");
    }
  }, [open, suggestion.id]);

  useEffect(() => {
    if (songs.data?.works) {
      setSongList([...songs.data.works]);
    }
  }, [songs.data?.works]);

  const findSuggestedMatches = useCallback(() => {
    if (!songs.data?.works)
      return;

    // Rank by similarity and take the best-scoring match, not just the
    // first one to clear the threshold — array order is otherwise
    // arbitrary and can put a weaker match ahead of an exact one.
    const bestMatch = songs.data.works
      .map(s => ({ song: s, score: compareTwoStrings(s.title, suggestion.title) }))
      .filter(m => m.score > 0.7)
      .sort((a, b) => b.score - a.score)[0]
      ?.song ?? null;

    setSuggestedSong(bestMatch);
    setSelectedSong(bestMatch);
  }, [songs.data?.works, compareTwoStrings, suggestion.title]);

  // Run the fuzzy match once, on entry into the "existing song" step.
  useEffect(() => {
    if (step === "existing" && songs.data?.works) {
      findSuggestedMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, songs.data?.works]);

  const updateSuggestionStatus = (status: number): void => {
    setPending(true);
    updateSuggestionMutation.reset();
    updateSuggestionMutation.mutate({ status, id: suggestion.id }, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["useGetMySongs"], exact: false });
        setPending(false);
        onSuggestionResolved();
        onOpenChange(false);
      },
      onError: (error: Error) => {
        console.error(`update suggestion ${error.message}`);
        setPending(false);
      },
    });
  };

  const addRecordingMeta = (rid: string): void => {
    setMutationErrorMessage("");
    setValidationErrorMessage("");

    recordingMetaMutation.reset();
    const data = {
      id: rid,
      spotify_id: suggestion.spotify_song_id,
      mp3: null,
      thumb_url: parseSuggestionThumbUrl(suggestion.images),
      song_id: songTemp.data?.workcode,
    };

    recordingMetaMutation.mutate(data, {
      onSuccess: (response) => {
        setValidationErrorMessage("");
        setMutationErrorMessage("");

        if (response.failedvalidation) {
          const errorMessages = response.messages?.join(", ") ?? "Validation failed, but no specific messages were provided.";
          setValidationErrorMessage(errorMessages);
          setPending(false);
        }
        else {
          toast.success(String(t("songs:suggestion-details-toast-success")));
          sendProperty({
            property: "last_recording_added",
            value: "CURRENT_DATE",
            email: profile.data?.email,
          });
          sendEvent({ property: "pe14489857_recording_added" });
          void songs.refetch();
          updateSuggestionStatus(2);
        }
      },
      onError: (error: Error) => {
        console.error(`Error during recording meta update mutation: ${error.message}`, error);
        setValidationErrorMessage("");
        setMutationErrorMessage(error.message || "An unknown error occurred during the meta update.");
        setPending(false);
      },
    });
  };

  const addRecording = (): void => {
    if (songTemp.data == null)
      return;

    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    recordingCreateMutation.reset();

    const data = {
      artistaccountcode: selectedAccount,
      title: sanitizeInput(suggestion.title),
      performingartist: sanitizeInput(suggestion.artists.split(",")[0]),
      isrc: suggestion.isrc,
      releasedate: suggestion.releasedate,
      workcode: songTemp.data.workcode,
      uicbarcode: short.generate().substring(0, 13),
      spotifyidentifier: suggestion.spotify_song_id,
      spotifypopularity: suggestion.popularity,
      spotifypopularitylastupdatedutc: suggestion.spotify_song_id == null
        ? null
        : new Date().toISOString(),
      etag: songTemp.data.etag,
    };

    recordingCreateMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.messages?.join(", ") ?? "");
          setPending(false);
        }
        else if (response.model?.uicbarcode != null) {
          addRecordingMeta(response.model.uicbarcode);
        }
      },
      onError: (error: Error) => {
        console.error(`error on addRecording ${error.message}`);
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={step === "new" ? "max-w-6xl" : "max-w-2xl"}>
        {step !== "new" && (
          <>
            <DialogHeader>
              <DialogTitle>{suggestion.title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-wrap text-ink-secondary">
              <div className="w-full sm:w-1/2">
                <Text as="p" className="text-xs">
                  <span className="font-semibold uppercase">
                    {t("songs:performed-by")}
                    :
                  </span>
                  {" "}
                  {suggestion.artists.replace(/&#44;/g, ",")}
                </Text>
              </div>
              <div className="w-full sm:w-1/2">
                <Text as="p" className="text-xs">
                  <span className="font-semibold uppercase">
                    {t("songs:duration")}
                    :
                  </span>
                  {" "}
                  {Math.floor(suggestion.duration / 1000 / 60)}
                  {" "}
                  {t("common:minutes")}
                  {" "}
                  {Math.floor((suggestion.duration / 1000) % 60)}
                  {" "}
                  {t("common:seconds")}
                </Text>
              </div>
            </div>
          </>
        )}

        {pending && (
          <div className="flex justify-center py-8">
            <LoadingSmall />
          </div>
        )}

        {!pending && step === "choice" && (
          <div className="flex flex-col gap-4 mt-4">
            <Text as="p" className="text-ink-secondary">
              {t("songs:song-choice-prompt", "Is this a brand new song, or does it already exist in your account?")}
            </Text>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <Button
                variant="secondary"
                label={String(t("songs:song-choice-existing", "Existing Song"))}
                onClick={() => setStep("existing")}
              />
              <Button
                variant="primary"
                label={String(t("songs:song-choice-new", "New Song"))}
                onClick={() => setStep("new")}
              />
            </div>
          </div>
        )}

        {!pending && step === "new" && (
          <SongsCreateUpdate
            suggestion={suggestion}
            closeForm={() => setStep("choice")}
            onComplete={() => updateSuggestionStatus(1)}
          />
        )}

        {!pending && step === "existing" && (
          <div className="flex flex-col gap-4 mt-4">
            <Button
              variant="link"
              icon={<IconChevronLeft size="md" className="stroke-current" />}
              label={String(t("common:back", "Back"))}
              onClick={() => setStep("choice")}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 border rounded-lg border-stroke-subtle bg-layer-disabled">
                <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                  {t("songs:existing-song-label", "Existing Song")}
                </Text>
                {selectedSong
                  ? (
                      <>
                        <Text as="p" className="text-lg font-semibold">{selectedSong.title}</Text>
                        {songTemp.isSuccess && (
                          <>
                            <Text as="p" className="mt-1 text-xs">
                              {songTemp.data.performingartists
                                .map((p: { performingartistname: string }) => p.performingartistname.replace(/&#44;/g, ","))
                                .join(", ")}
                            </Text>
                            <Text as="p" className="mt-1 text-xs">
                              ISWC:
                              {" "}
                              {songTemp.data.iswc != null && songTemp.data.iswc !== "" ? songTemp.data.iswc : "-"}
                            </Text>
                          </>
                        )}
                        {suggestedSong?.workcode === selectedSong.workcode && (
                          <Text as="p" className="flex items-center gap-1 mt-2 text-xs italic text-ink-success">
                            <IconInfo className="w-3 h-3 shrink-0" />
                            {t("songs:looks-like-a-match")}
                          </Text>
                        )}
                      </>
                    )
                  : (
                      <Text as="p" className="text-sm italic text-ink-secondary">
                        {t("songs:select-song-to-link-prompt", "Select a song below to link this suggestion to")}
                      </Text>
                    )}
              </div>

              <div className="p-4 border-2 rounded-lg border-stroke-primary bg-action-primary/5">
                <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                  {t("songs:new-recording-label", "New Recording")}
                </Text>
                <Text as="p" className="text-lg font-semibold">{suggestion.title}</Text>
                <Text as="p" className="text-sm text-ink-secondary">{suggestion.artists.replace(/&#44;/g, ",")}</Text>
              </div>
            </div>

            <Combobox
              smallFont
              label={String(t("songs:search-for-existing-and-select"))}
              data={songList.map(m => ({ value: m.workcode, label: m.title }))}
              value={selectedSong?.workcode}
              onValueChange={(workcode: string) => setSelectedSong(songList.find(s => s.workcode === workcode) ?? null)}
              searchByLabel
              placeholder={String(t("songs:search-for-existing-placeholder"))}
            />

            <FormError
              mutationError={mutationErrorMessage}
              validationError={validationErrorMessage}
            />

            <div className="flex flex-wrap items-center justify-end gap-2 mt-2">
              <Button
                variant="link"
                label={String(t("songs:cancel-and-discard"))}
                onClick={() => onOpenChange(false)}
              />
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                label={String(t("songs:add-recording"))}
                icon={selectedSong && songTemp.isLoading ? <Spinner status="pending" size="sm" /> : undefined}
                iconPosition="right"
                disabled={!selectedSong || pending || songTemp.data == null}
                onClick={() => addRecording()}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SongSuggestionActionModal;
